import React, { FC, memo, useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import Typography from '@mui/material/Typography';
import { ColumnDef } from '@tanstack/react-table';

import { modalActions } from '@contexts/common/ModalContext';

import { formatBNWithCommas } from '@utils/formaters.utils';
import { getTokenListForUi, isAddressesEqual } from '@utils/tokens.utils';

import Button from '@components/common/Button';
import Input from '@components/common/Input';
import ModalWrapper from '@components/common/Modal/ModalWrapper';
import Table from '@components/common/Table';
import { ROW_HEIGHT } from '@components/common/Table/table.constants';
import { getCellStyles } from '@components/common/Table/table.helpers';
import TokensIcon from '@components/common/TokensIcon';

import {
  DEFAULT_CRITERIA_MODE,
  DEFAULT_REVALIDATE_MODE,
  FieldNames,
} from '@constants/common/form.constants';
import { PATH_DEX_ETF } from '@constants/common/routes.constants';
import {
  Size,
  TableVariant,
  TokenSize,
  Variant,
} from '@constants/common/ui.constants';
import { TokenType } from '@constants/vaults/tokens.constants';
import {
  CHANGE_TAKE_PROFIT_HEADER,
  ChangeTakeProfitToken,
  CreateVaultsKeys,
} from '@constants/vaults/vault.constants';

import { CommonTokenDto, ExtendedCommonToken } from '@dto/common/tokens.dto';

import { ChangeProfitTokenModalProps } from '@app-types/common/modal.types';

import useStyles from './styles.module';

const ChangeTakeProfitTokenModal: FC<ChangeProfitTokenModalProps> = ({
  takeProfitToken,
  profitTokensWl,
  tokens,
  onChangeProfitToken,
}) => {
  const { classes } = useStyles();

  const profitTokensList = useMemo(
    () =>
      profitTokensWl.map((item) => {
        if (item.type === TokenType.ETF) {
          const token = tokens.etf[item.chainId][item.underlying as string];
          return {
            ...item,
            tokensList: token.innerTokens,
          };
        }
        return item;
      }) as ExtendedCommonToken[],
    [profitTokensWl, tokens],
  );

  const handleChange = useCallback(
    (token: CommonTokenDto) => {
      const currentToken = profitTokensWl.find((item) =>
        isAddressesEqual(item.address, token.address),
      );

      if (currentToken?.type === TokenType.ETF) {
        const tokenETF = tokens.etf[token.chainId][token.underlying as string];

        const updatedToken = { ...token, tokensList: tokenETF.innerTokens };
        onChangeProfitToken(updatedToken);
      } else if (currentToken) {
        onChangeProfitToken(currentToken);
      }

      modalActions.hide();
    },
    [profitTokensWl, tokens],
  );

  const { control, watch } = useForm({
    mode: DEFAULT_CRITERIA_MODE,
    reValidateMode: DEFAULT_REVALIDATE_MODE,
    defaultValues: {
      [FieldNames.TokenSymbol]: '',
    },
  });

  const searchTerm = useMemo(
    () => watch(FieldNames.TokenSymbol),
    [watch(FieldNames.TokenSymbol)],
  );

  const filteredTokensList = useMemo(
    () =>
      profitTokensList.filter((token) =>
        token.symbol.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [profitTokensList, searchTerm],
  );

  console.log(filteredTokensList, 'filteredTokensList');

  const columns = useMemo<ColumnDef<ExtendedCommonToken>[]>(
    () => [
      {
        size: 200,
        id: ChangeTakeProfitToken.Name,
        accessorKey: ChangeTakeProfitToken.Name,
        header: () => CHANGE_TAKE_PROFIT_HEADER[ChangeTakeProfitToken.Name],
        cell: (info) => getCellStyles(info.getValue<string>()),
        accessorFn: (token) => {
          const tokensIcons = () => {
            if (token.type === TokenType.ETF) {
              return (token?.tokensList || []).map(({ address, chainId }) => ({
                address,
                chainId,
              }));
            }
            return getTokenListForUi(token);
          };

          return (
            <div className={classes.token}>
              <TokensIcon tokens={tokensIcons()} size={TokenSize.Large} />
              <Typography variant="body2">{token.symbol}</Typography>
            </div>
          );
        },
      },
      {
        size: 130,
        id: ChangeTakeProfitToken.Price,
        accessorKey: ChangeTakeProfitToken.Price,
        header: () => CHANGE_TAKE_PROFIT_HEADER[ChangeTakeProfitToken.Price],
        cell: (info) => getCellStyles(info.getValue<string>()),
        accessorFn: (row) => `$${formatBNWithCommas(row.priceInUsd, 2)}`,
      },

      {
        size: 240,
        id: ChangeTakeProfitToken.Action,
        accessorKey: ChangeTakeProfitToken.Action,
        header: () => CHANGE_TAKE_PROFIT_HEADER[ChangeTakeProfitToken.Action],
        cell: (info) => getCellStyles(info.getValue<string>()),
        accessorFn: (token) => {
          const openFund = () => {
            window.open(
              `${PATH_DEX_ETF}/${token.underlying}/?chain=${token.chainId}`,
            );
          };

          return (
            <div className={classes.btns}>
              {token.type === TokenType.ETF && (
                <Button
                  size={Size.Small}
                  variant={Variant.Secondary}
                  onClick={openFund}
                >
                  View fund
                </Button>
              )}
              <Button
                size={Size.Small}
                variant={Variant.Contained}
                onClick={() => handleChange(token)}
                disabled={isAddressesEqual(
                  takeProfitToken.address,
                  token.address,
                )}
              >
                Select
              </Button>
            </div>
          );
        },
      },
    ],
    [takeProfitToken],
  );

  return (
    <ModalWrapper title="Change take profit token">
      <form className={classes.inner}>
        <Controller
          control={control}
          name={FieldNames.TokenSymbol}
          render={({
            field: { value, onBlur, onChange },
            fieldState: { error },
          }) => (
            <Input
              value={value}
              onBlur={onBlur}
              onChange={onChange}
              placeholder="Search"
              inputError={error?.message}
            />
          )}
        />
        <Table
          data={filteredTokensList}
          columns={columns}
          isLoading={false}
          isFetching={false}
          tableHeight={237}
          rowHeight={ROW_HEIGHT}
          totalRowCount={filteredTokensList.length}
          variant={TableVariant.Secondary}
          pinnedRightColumn={[CreateVaultsKeys.Action]}
        />
      </form>
    </ModalWrapper>
  );
};

export default memo(ChangeTakeProfitTokenModal);
